// components/workspace-timeline/WorkspaceTimelineVolumeOverlay.tsx
import React, { useCallback, useMemo } from 'react';
import {
  AUDIO_GAIN_SLIDER_MAX,
  AUDIO_GAIN_SLIDER_MIN,
  audioGainToSliderPercent,
  clamp,
} from './presentation';

interface WorkspaceTimelineVolumeOverlayProps {
  trackId: string;
  trackName: string;
  startMs: number;
  activeDurationMs: number;
  audioGain: number;
  laneLeftColumnWidthPx: number;
  timelineRenderRangeStartMs: number;
  timelineRenderRangeMs: number;
  onChange: (trackId: string, trackName: string, currentAudioGain: number, nextPercent: number) => void;
  onFinalize: (trackId: string) => void;
}

interface WorkspaceTimelineVolumeOverlayPresentation {
  startPercent: number;
  widthPercent: number;
  sliderPercent: number;
  title: string;
  ariaLabel: string;
}

const TIMELINE_VOLUME_OVERLAY_LANE_STYLE: React.CSSProperties = {
  height: '10px',
  position: 'relative',
};
const TIMELINE_VOLUME_OVERLAY_INPUT_STYLE: React.CSSProperties = {
  width: '100%',
  height: '10px',
  accentColor: '#22c55e',
  cursor: 'ew-resize',
};

export const WorkspaceTimelineVolumeOverlay = React.memo(({
  trackId,
  trackName,
  startMs,
  activeDurationMs,
  audioGain,
  laneLeftColumnWidthPx,
  timelineRenderRangeStartMs,
  timelineRenderRangeMs,
  onChange,
  onFinalize,
}: WorkspaceTimelineVolumeOverlayProps) => {
  const presentation = useMemo<WorkspaceTimelineVolumeOverlayPresentation>(() => {
    const rawStartPercent = timelineRenderRangeMs > 0
      ? ((startMs - timelineRenderRangeStartMs) / timelineRenderRangeMs) * 100
      : 0;
    const rawWidthPercent = timelineRenderRangeMs > 0
      ? Math.max((activeDurationMs / timelineRenderRangeMs) * 100, 0.8)
      : 0.8;
    const sliderPercent = audioGainToSliderPercent(audioGain);
    return {
      startPercent: clamp(rawStartPercent, 0, 100),
      widthPercent: clamp(rawWidthPercent, 0, 100),
      sliderPercent,
      title: `Volume ${sliderPercent}% (50% = original)`,
      ariaLabel: `Adjust volume for ${trackName}`,
    };
  }, [activeDurationMs, audioGain, startMs, timelineRenderRangeMs, timelineRenderRangeStartMs, trackName]);
  const rootStyle = useMemo<React.CSSProperties>(() => ({
    gridTemplateColumns: `${laneLeftColumnWidthPx}px minmax(0,1fr)`,
    marginTop: '-2px',
  }), [laneLeftColumnWidthPx]);
  const sliderShellStyle = useMemo<React.CSSProperties>(() => ({
    position: 'absolute',
    left: `${presentation.startPercent}%`,
    width: `${presentation.widthPercent}%`,
    minWidth: '34px',
    top: '0px',
    height: '10px',
    display: 'flex',
    alignItems: 'center',
  }), [presentation.startPercent, presentation.widthPercent]);
  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  }, []);
  const handleContextMenu = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);
  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(trackId, trackName, audioGain, Number(event.currentTarget.value));
  }, [audioGain, onChange, trackId, trackName]);
  const handleFinalize = useCallback(() => {
    onFinalize(trackId);
  }, [onFinalize, trackId]);

  return (
    <div className="grid w-full items-center gap-2" style={rootStyle}>
      <div />
      <div
        onMouseDown={handleMouseDown}
        onContextMenu={handleContextMenu}
        style={TIMELINE_VOLUME_OVERLAY_LANE_STYLE}
      >
        <div style={sliderShellStyle}>
          <input
            type="range"
            min={AUDIO_GAIN_SLIDER_MIN}
            max={AUDIO_GAIN_SLIDER_MAX}
            step={1}
            value={presentation.sliderPercent}
            onChange={handleChange}
            onMouseUp={handleFinalize}
            onTouchEnd={handleFinalize}
            onBlur={handleFinalize}
            style={TIMELINE_VOLUME_OVERLAY_INPUT_STYLE}
            aria-label={presentation.ariaLabel}
            title={presentation.title}
          />
        </div>
      </div>
    </div>
  );
});

WorkspaceTimelineVolumeOverlay.displayName = 'WorkspaceTimelineVolumeOverlay';
